import React from 'react';
import { Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  SimpleFormIterator,
  useSimpleFormIterator,
  useSimpleFormIteratorItem
} from 'react-admin';

// react-admin's defaults are bare circular icons with no labels, and the
// clear-all sits right next to add, so one misclick wipes every entry.
const AddItemButton = ({ label }: { label: string }) => {
  const { add } = useSimpleFormIterator();
  return (
    <Button onClick={() => add()} startIcon={<AddIcon />} size="small" variant="outlined">
      {label}
    </Button>
  );
};

const RemoveItemButton = ({ label }: { label: string }) => {
  const { remove } = useSimpleFormIterator();
  const { index } = useSimpleFormIteratorItem();
  return (
    <Button
      onClick={() => remove(index)}
      startIcon={<DeleteOutlineIcon />}
      size="small"
      color="error"
    >
      {label}
    </Button>
  );
};

interface Props {
  /** Goes on the add button, e.g. "Add related person". */
  addLabel: string;
  /** Goes on each row's remove button. */
  removeLabel?: string;
  /** Singular noun for the row heading, e.g. "Related person" -> "Related person 1". */
  itemLabel?: string;
  /** One compact row per entry rather than a block. Suits emails and phones. */
  inline?: boolean;
  /** Lay each row out as a two-column grid. Ignored when inline. */
  grid?: boolean;
  /** Reordering is off unless the order carries meaning. */
  reorderable?: boolean;
  children: React.ReactNode;
}

export const RepeatableInput = ({
  addLabel,
  removeLabel = 'Remove',
  itemLabel,
  inline = false,
  grid = false,
  reorderable = false,
  children
}: Props) => (
  <SimpleFormIterator
    inline={inline}
    addButton={<AddItemButton label={addLabel} />}
    removeButton={<RemoveItemButton label={removeLabel} />}
    disableClear
    disableReordering={!reorderable}
    getItemLabel={
      itemLabel
        ? (index: number) => (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap', pr: 1 }}
            >
              {`${itemLabel} ${index + 1}`}
            </Typography>
          )
        : false
    }
    sx={{
      '& .RaSimpleFormIterator-line': {
        alignItems: 'flex-start',
        // A compact row only needs separating; a block of fields reads better
        // as its own card.
        ...(inline
          ? { borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }
          : {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 2,
              mb: 1.5,
              bgcolor: 'background.paper'
            })
      },
      '& .RaSimpleFormIterator-line:last-of-type': {
        ...(inline ? { borderBottom: 'none' } : {})
      },
      '& .RaSimpleFormIterator-index': {
        pt: 1
      },
      ...(grid && !inline
        ? {
            '& .RaSimpleFormIterator-form': {
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              columnGap: 2,
              rowGap: 1.5,
              width: '100%'
            }
          }
        : {}),
      '& .RaSimpleFormIterator-action': {
        pt: inline ? 0.5 : 1,
        visibility: 'visible'
      }
    }}
  >
    {children}
  </SimpleFormIterator>
);

export default RepeatableInput;
